import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import objectSupport from 'dayjs/plugin/objectSupport';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMediaQuery } from 'react-responsive';

import { fetchDocumentTemplates } from 'api/documents';
import { getMembers, getLeadChecklistMappingSettings } from 'api/account';
import {
  updateLead as updateLeadAction,
  fetchLead,
  deleteLead,
  getLeadComments,
  createLeadComment,
  updateLeadComment,
  deleteLeadComment,
  getChecklistItems,
  createChecklistFromTemplate,
  createLeadChecklistItem,
  updateChecklistItem as updateChecklistItemAction,
  deleteLeadChecklistItem,
  addChecklistAssignment,
  removeChecklistAssignment,
  reorderChecklistItem as reorderChecklistItemAction,
  getChecklistComments as getChecklistCommentsAction,
  addChecklistComment,
  updateChecklistComment,
  deleteChecklistComment,
  getChecklistReminders,
  createChecklistReminder,
  updateChecklistReminder,
  deleteChecklistReminder,
} from 'containers/Leads/Lead/actions';
import { getInvoiceTemplates } from 'containers/Settings/Invoices/actions';
import { getQuoteTemplates } from 'containers/Settings/Quotes/actions';
import { getLeadChecklistTemplates } from 'containers/Settings/Leads/actions';
import {
  getLeadAssignments as getLeadAssignmentsAction,
  getLeadCustomPreferences as getLeadCustomPreferencesAction,
} from 'containers/Leads/actions';
import {
  CREATE_LEAD_ASSIGNMENT,
  DELETE_LEAD_ASSIGNMENT,
} from 'containers/Leads/types';
import {
  TIME_ZONES,
  TIME_ZONES_ORDER,
  DATE_FORMATS,
} from 'utils/constants';
import { prepareLocationForApi } from 'utils/location';
import { timeFormatValidation, getTimeList, getDayjsTimeFormat } from 'utils/time';
import { getDayjsDateFormat } from 'utils/date';
import { objToCamelCase } from 'utils/general';
import { scrollToElementIfNeeded } from 'utils/scroll';
import Avatar from 'components/Avatar';
import Accordion from 'components/Accordion';
import LocationSelect from 'components/LocationSelect';
import Clickwrap from 'components/Clickwrap';
import InputField from 'components/FormFields/InputField';
import ContactField from 'components/ContactField/ContactField';
import CurrencyInput from 'components/FormFields/CurrencyInput';
import FormSelect from 'components/FormFields/FormSelect';
import MemberAssignment from 'pages/Leads/MemberAssignment/index';
import TimeSettingFormItem from 'components/FormFields/TimeSettingFormItem';
import CalendarWidget from 'components/FormFields/CalendarWidget';
import HorizontalMenu from 'components/Menus/HorizontalMenu';
import DeleteConfirmation from 'components/DeleteConfirmation';
import Checklist from 'components/Checklist';
import APModal from 'components/Modal/APModal';
import HeaderString from 'components/HeaderString';
import { Loading } from 'components/Loading';
import { useAction } from 'hooks/useAction';
import LeadRecordActivity from './LeadRecord.Activity';
import LeadRecordDocuments from './LeadRecord.Documents';
import { LeadRecordActionButton } from './LeadRecord.ActionButton';
import LeadRecordProjectItem from './LeadRecordProjectItem';
import LeadRecordNotes from './LeadRecordNotes';
import { transformChecklist } from './utils/transforms';
import ThreadsCenter from '../../LeadsEmailer/components/ThreadsCenter';
import { validateLeadUpdate } from './utils/leadValidation';
import { validateChecklistField } from './utils/checklistValidation';
import LeadRecordContactInfo from './LeadRecordContactInfo';
import ArchiveModal from '../Leads.ArchiveModal';

import './lead-record.scss';

dayjs.extend(customParseFormat);
dayjs.extend(objectSupport);
dayjs.extend(isSameOrAfter);

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
const DEFAULT_TIMEZONE = 'UTC';

const PROJECT_TYPES = [
  {
    value: 'wedding',
    label: 'Wedding',
  },
  {
    value: 'other',
    label: 'Event or Project',
  },
];

export const ACTIVITY_LOG = 'activity-log';
export const CHECKLIST = 'checklist';
export const FILES = 'files';
export const NOTES = 'notes';
export const EMAILS = 'emails';

const getPreferencesByType = (preferences = [], type, message) => {
  const filteredCustomPreferences = preferences
    .filter((pref) => pref.kind === type && pref.visibility)
    .map((pref) => {
      const value = pref.id;
      const label = pref.name;
      return {
        value,
        label,
      };
    });

  if (filteredCustomPreferences.length === 0) {
    filteredCustomPreferences.unshift({
      value: -1,
      label: message,
    });
  }

  return filteredCustomPreferences;
};

const avatarContext = require.context(
  'assets/images/leads/nature',
  false,
  /\.svg$/,
);

const getAvatarImageById = (id) => {
  const mod = avatarContext(`./nature-${id}.svg`);

  return mod.default ?? mod;
};

const sortByPrimary = (items, primaryId) => {
  if (!primaryId || !items?.length) return items || [];
  const idx = items.findIndex(item => item.id === primaryId);
  if (idx <= 0) return items;
  return [items[idx], ...items.slice(0, idx), ...items.slice(idx + 1)];
};

const LeadRecord = () => {
  const dispatch = useDispatch();

  const { orgId, leadId } = useParams();
  const history = useHistory();
  const [isEditingContactName, setEditingContactName] = useState(false);
  const contactRef = useRef(null);
  const tabsRef = useRef(null); // For autoscrolling if needed.
  const { activeTab, itemId: selectedItemId } = useParams();

  const userInfo = useSelector((state) => state.global.userInfo);
  const { invoiceTemplates } = useSelector((state) => state.settings.invoiceSettings);
  const { quoteTemplates } = useSelector((state) => state.settings.quoteSettings);
  const leadsById = useSelector((state) => state.leads.leadsById);
  const leadCustomFields = useSelector((state) => state.leads.leadCustomFields) || {};
  const templateList = useSelector((state) => state.settings.leadSettings.checklistTemplates);
  const leadData = leadsById[leadId];
  const { organization, integrations = [], integrationsEmails: userEmail = [] } = userInfo || {};
  const { datePreference, timePreference: timeFormat = '12', currency } = organization;

  // If all the integrations for the current user are disconnected, we don't want them to be able to reply.
  const allDisconnected = integrations.every(integration => integration.grantStatus === 'disconnected');
  const readOnlyMode = (userInfo?.permissions['lead.view'] && userEmail.length === 0) || allDisconnected;

  // Assume if no lead data, it's still being fetched.
  const isLoading = !leadData?.lead;

  const {
    lead,
    contact,
    leadCustomStatuses: customStatuses = [],
    projects = [],
    quotes: quotesList,
    invoices: invoicesList,
    documents: documentList,
    // addresses,
    location,
    customPreferences,
    checklists,
    comments: leadComments,
  } = leadData || {};

  const {
    contactFirstName,
    contactLastName,
    allDay: allDayEvent,
    dateInquiry: inquiryDate,
    endTime = '10:00:00',
    estimatedBudget: budget,
    estimatedGuestCount: guestCount,
    lastContactedDate,
    leadCustomStatusId,
    leadSourceId: sourceType,
    notes,
    party,
    projectName,
    projectType,
    serviceTypeId: serviceType,
    startTime = '9:00:00',
    timezone,
    venueName: venue,
    weddingDate: eventDate,
    contactCompanyName,
    role,
  } = lead || {};

  const {
    emails: rawEmails,
    phones: rawPhones,
    addresses: rawAddresses,
    websites: rawWebsites,
    primaryEmailId,
    primaryPhoneId,
    primaryAddressId,
    primaryWebsiteId,
  } = contact || { emails: [], phones: [], addresses: [], websites: [] };

  const emails = sortByPrimary(rawEmails, primaryEmailId);
  const phones = sortByPrimary(rawPhones, primaryPhoneId);
  const addresses = sortByPrimary(rawAddresses, primaryAddressId);
  const websites = sortByPrimary(rawWebsites, primaryWebsiteId);

  const contactId = contact?.id || 0;
  const avatarImageId = contactId % 5 + 1;
  const avatarImage = getAvatarImageById(avatarImageId);

  const contactName = `${contactFirstName ?? ''} ${contactLastName ?? ''}`.trim() || '(No name)';

  const {
    items = [],
    assignments: checklistAssignmentsByUser,
    comments: commentsByChecklistItemId,
    reminders: checklistRemindersByTask = {},
  } = checklists || {};
  // Transform the data to fit the checklist interface.
  const checklistItems = useMemo(() => {
    return items.map((item) => transformChecklist(item));
  }, [items]);

  // Send the project id to documents if the project has a v2 client.
  const v2ClientProject = useMemo(() => {
    if (projects.length === 1 && projects[0].isV2Client) {
      return projects[0];
    }
    return null;
  }, [projects]);
  // We only want to lock down project type if lead is a v2 client and
  // the lead has a project created.
  const shouldLockProjectType = !!v2ClientProject;

  // Data comes from the api with the assignments by login id. We want
  // them by task id to join into the task object.
  const checklistAssignments = useMemo(() => {
    if (!checklistAssignmentsByUser) return {};
    const assignmentsByTaskId = {};
    Object.keys(checklistAssignmentsByUser).forEach(loginId => {
      checklistAssignmentsByUser[loginId].forEach(_assignment => {
        const { taskId } = _assignment;
        if (assignmentsByTaskId[taskId]) {
          assignmentsByTaskId[taskId] = [...assignmentsByTaskId[taskId], _assignment];
        } else {
          assignmentsByTaskId[taskId] = [_assignment];
        }
      });
    });
    return assignmentsByTaskId;
  }, [checklistAssignmentsByUser]);

  const leadUserDefinedFields = useMemo(() => {
    if (leadId in leadCustomFields) {
      return leadCustomFields[leadId];
    }
    return [];
  }, [leadCustomFields]);

  const serviceTypes = useMemo(() => {
    return getPreferencesByType(
      customPreferences,
      'service-type',
      'Click here to add and manage service types in your lead settings',
    );
  }, [customPreferences]);

  const sourceTypes = useMemo(() => {
    return getPreferencesByType(
    customPreferences,
    'lead-source',
    'Click here to add and manage lead sources in your lead settings',
    );
  }, [customPreferences]);

  const lostReasons = useMemo(() => {
    const reasons = getPreferencesByType(
      customPreferences,
      'reason-for-not-booking',
    );
    // Modal expects different object shape.
    return reasons.map((reason) => ({ id: reason.value, name: reason.label }));
  }, [customPreferences]);

  const lostStatus = useMemo(() => customStatuses.find((status) => status.name === 'Lost'), [customStatuses]);

  const userDefinedFields = useMemo(() => {
    return leadUserDefinedFields ?
      leadUserDefinedFields.sort((a, b) => a.order - b.order)
        .filter(field => field.isActive) : [];
  }, [leadUserDefinedFields]);

  const leadFullName = [contactFirstName, contactLastName].join(' ');
  const avatarName = [contactFirstName?.charAt(0), contactLastName?.charAt(0)].join('')
    .toUpperCase();

  // #TODO: We might want to search array using primary ids from contact object.
  const [primaryEmail] = emails || [];
  const [primaryPhone] = phones || [];
  const leadEmail = primaryEmail?.fieldValue || '';
  const leadEmailId = primaryEmail?.id;
  const leadPhone = primaryPhone?.fieldValue || '';
  const leadPhoneId = primaryPhone?.id;

  const [orgMembers, setOrgMembers] = useState([]);

  const orgDateFormat = getDayjsDateFormat(organization.datePreference);
  const orgTimeFormat = getDayjsTimeFormat(organization.timePreference);
  const [templateModal, setTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [documentTemplates, setDocumentTemplates] = useState([]);

  const [leadChecklistMappingSettings, setLeadChecklistMappingSettings] = useState(null);
  const [checklistTemplateModal, setChecklistTemplateModal] = useState(false);
  const [checklistTemplate, setChecklistTemplate] = useState(null);

  const getTabFromHash = () => {
    const { hash, search } = window.location;
    if (hash && hash.length > 1) {
      const fromHash = decodeURIComponent(hash.slice(1));
      const validTabs = new Set([ACTIVITY_LOG, CHECKLIST, FILES, NOTES, EMAILS]);
      if (validTabs.has(fromHash)) {
        history.replace(`/app/v2/${orgId}/leads/lead/${leadId}/${fromHash}${search || ''}`);
        return fromHash;
      }
    }
    return null;
  };

  const getLeadTab = () => {
    const hashTab = getTabFromHash();
    if (hashTab) return hashTab;

    switch (activeTab) {
      case 'activity-log':
        return ACTIVITY_LOG;
      case 'sales-workflow':
        return CHECKLIST;
      case 'emails':
        return EMAILS;
      case 'files':
        return FILES;
      default:
        return NOTES;
    }
  };

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const [tab, setTab] = useState(getLeadTab());

  // This useEffect listens to changes in the URL,
  // specifically the activeTab param, and updates the tab state accordingly.
  // This ensures that when users navigate using browser back/forward buttons,
  // or directly access a URL with a specific tab, the correct tab content is displayed.
  useEffect(() => {
    const tabMap = {
      'notes': NOTES,
      'activity-log': ACTIVITY_LOG,
      'sales-workflow': CHECKLIST,
      'emails': EMAILS,
      'files': FILES,
    };
    if (activeTab && tabMap[activeTab] && tabMap[activeTab] !== tab) {
      setTab(tabMap[activeTab]);
    }
  }, [activeTab]);

  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [openNewEmailModal, setOpenNewEmailModal] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmOpened, setIsDeleteConfirmOpened] = useState(false);
  const [archiveOpened, setArchiveOpened] = useState(false);

  const timeList = getTimeList(timeFormat);
  const dateFormat = datePreference || DEFAULT_DATE_FORMAT;
  const dateFormatLabel = DATE_FORMATS.find((format) => format.value === dateFormat)?.label ?? DEFAULT_DATE_FORMAT;

  const [primaryParty, secondaryParty] = party || [];
  const [primaryPartyRole, primaryPartyFirstName, primaryPartyLastName] = primaryParty || [];
  const [secondaryPartyRole, secondaryPartyFirstName, secondaryPartyLastName] = secondaryParty || [];

  // TODO: Duplicated from CreateLeadForm -- consider moving to utils.
  const getRoleOptions = useCallback(() => {
    const roleList = ['bride', 'groom', 'guest-of-honor', 'partner', 'bridegroom'];
    const transform = (str) => str
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return roleList.map((_role) => {
      return (
        <option value={_role}>{transform(_role)}</option>
      );
    });
  }, []);

  const nextParty = (rank, field, value) => {
    const fieldsIdx = { role: 0, firstName: 1, lastName: 2 };
    const idx = fieldsIdx[field];
    const copy = [...party];
    copy[rank][idx] = value;

    return copy;
  };

  const updateLeadDispatch = useAction(updateLeadAction);
  const updateChecklistItemDispatch = useAction(updateChecklistItemAction);
  const reorderChecklistItemDispatch = useAction(reorderChecklistItemAction);

  const tabs = [
    {
      id: 'notes',
      name: 'notes',
      onClick: () => {
        setTab(NOTES);
        history.replace(`/app/v2/${orgId}/leads/lead/${leadId}/notes`);
      },
      active: tab === NOTES,
    },
    {
      id: 'activity-log',
      name: 'activity log',
      onClick: () => {
        setTab(ACTIVITY_LOG);
        history.replace(`/app/v2/${orgId}/leads/lead/${leadId}/activity-log`);
      },
      active: tab === ACTIVITY_LOG,
    },
    {
      id: 'checklist',
      name: 'Sales Workflow',
      onClick: () => {
        setTab(CHECKLIST);
        history.replace(`/app/v2/${orgId}/leads/lead/${leadId}/sales-workflow`);
      },
      active: tab === CHECKLIST,
    },
    {
      id: 'emails',
      name: 'Email',
      onClick: () => {
        setTab(EMAILS);
        history.replace(`/app/v2/${orgId}/leads/lead/${leadId}/emails`);
      },
      active: tab === EMAILS,
    },
    {
      id: 'files',
      name: 'files',
      onClick: () => {
        setTab(FILES);
        history.replace(`/app/v2/${orgId}/leads/lead/${leadId}/files`);
      },
      active: tab === FILES,
    },
  ];

  const goBack = () => {
    if (history.location.search.includes('return_to')) {
      history.goBack();
      return;
    }
    const leadroute = localStorage.getItem('leadroute');
    if (leadroute) {
      history.push(`/app/v2/${orgId}/leads/status/${leadroute}`);
    } else {
      history.push(`/app/v2/${orgId}/leads`);
    }
  };

  // #TODO: Add this to redux.
  const getDocumentTemplates = async () => {
    const response = await fetchDocumentTemplates(orgId);
    if (response.ok && response.data.length) {
      setDocumentTemplates(response.data);
    }
  };

  const updateLeadFields = (newLeadData = {}) => {
    const { validatedData, invalidFields } = validateLeadUpdate(
      newLeadData,
      lead,
      { serviceTypes, leadSources: sourceTypes },
    );

    const updateData = { lead: validatedData };

    // Update project data if it exists and is a v2 client.
    if (projects.length === 1 && projects[0].isV2Client) {
      const [project] = projects || [];
      const newProject = {
        ...project,
        ... !!validatedData.projectName && { name: validatedData.projectName },
        ... !!validatedData.weddingDate && { weddingDate: validatedData.weddingDate },
      };
      updateData.projects = [newProject];
    }

    updateLeadDispatch({ orgId, leadId, updateData });
    return invalidFields;
  };

  const updateLocationField = (locationData) => {
    const updateData = { location: locationData };
    updateLeadDispatch({ orgId, leadId, updateData });
  };

  const handleEmailUpdate = async (e) => {
    const newEmail = e.target.value;
    if (newEmail === leadEmail) return;

    // Validate email
    const { validatedData, invalidFields } = validateLeadUpdate(
      { email: newEmail },
      { email: leadEmail },
    );

    if (invalidFields.includes('email')) {
      e.target.value = leadEmail;
      return;
    }

    updateLeadDispatch({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          emails: [{ ...emails[0], fieldValue: validatedData.email }],
        },
      },
      emailUpdate: {
        emailId: leadEmailId,
        fieldValue: validatedData.email,
      },
    });
  };


  const handlePhoneUpdate = async (e) => {
    const newPhone = e.target.value;
    if (newPhone === leadPhone) return;

    // Validate phone
    const { validatedData, invalidFields } = validateLeadUpdate(
      { phone: newPhone },
      { phone: leadPhone },
    );

    if (invalidFields.includes('phone')) {
      e.target.value = leadPhone;
      return;
    }

    updateLeadDispatch({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          phones: [{ ...phones[0], fieldValue: validatedData.phone }],
        },
      },
      phoneUpdate: {
        phoneId: leadPhoneId,
        fieldValue: validatedData.phone,
      },
    });
  };

  const handleEmailTypeChange = ({ contactType, fieldLabel }) => {
    updateLeadDispatch({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          emails: [{
            ...emails[0],
            apLabelId: contactType,
            fieldLabel: contactType === 'custom' ? fieldLabel : null,
          }],
        },
      },
      emailUpdate: {
        emailId: leadEmailId,
        fieldValue: leadEmail,
        contactType,
        fieldLabel: contactType === 'custom' ? fieldLabel : null,
      },
    });
  };

  const handlePhoneTypeChange = ({ contactType, fieldLabel }) => {
    updateLeadDispatch({
      orgId,
      leadId,
      updateData: {
        contact: {
          ...contact,
          phones: [{
            ...phones[0],
            apLabelId: contactType,
            fieldLabel: contactType === 'custom' ? fieldLabel : null,
          }],
        },
      },
      phoneUpdate: {
        phoneId: leadPhoneId,
        fieldValue: leadPhone,
        contactType,
        fieldLabel: contactType === 'custom' ? fieldLabel : null,
      },
    });
  };

  const handleActivityClick = (kind, params) => {
    switch(kind) {
      case 'email': {
        const { threadId } = params || {};
        if (threadId) {
          setTab(EMAILS);
          history.replace(`/app/v2/${orgId}/leads/lead/${leadId}/emails#${threadId}`);
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const handleAddComment = (comment) => {
    dispatch(createLeadComment({ orgId, leadId, comment }));
  };

  const handleUpdateComment = (commentId, comment) => {
    dispatch(updateLeadComment({ orgId, leadId, commentId, comment }));
  };

  const handleDeleteComment = (commentId) => {
    dispatch(deleteLeadComment({ orgId, leadId, commentId }));
  };

  const openChecklistTemplateModal = () => {
    dispatch(getLeadChecklistTemplates(orgId));
    setTemplateModal(true);
  };

  const addTask = (itemId) => {
    dispatch(createLeadChecklistItem(orgId, leadId, itemId));
  };

  const handleDeleteTask = (taskId) => {
    dispatch(deleteLeadChecklistItem(orgId, leadId, taskId));
  };

  const handleCreateChecklistFromTemplate = () => {
    setTemplateModal(false);
    dispatch(createChecklistFromTemplate({ orgId, leadId, templateId: selectedTemplate }));
  };

  const fetchOrgMembers = async () => {
    const response = await getMembers(orgId);

    if (response.ok && response.data) {
      setOrgMembers(objToCamelCase(response.data, { ignore: ['effectivePermissions'] }));
    }
  };

  const fetchLeadChecklistMappingSettings = async () => {
    const response = await getLeadChecklistMappingSettings(orgId);

    if (response.ok && response.data) {
      setLeadChecklistMappingSettings(response.data);
    }
  };

  const getLeadAssignments = useAction(getLeadAssignmentsAction);
  const getLeadCustomPreferences = useAction(getLeadCustomPreferencesAction);
  const leadAssignments = useSelector(
    (state) => state.leads.leadAssignments?.[leadId] ?? [],
  );

  const assignedIds = useMemo(
    () => new Set(leadAssignments.map((a) => a.loginId)),
    [leadAssignments],
  );

  const assignees = useMemo(() => {
    return orgMembers.map((member) => {
      const { effectivePermissions: permissions } = member;

      // Org member has access to all leads if lead.all is true.
      // Otherwise can be assigned with leads.full-access.
      if (permissions['lead.all'] || permissions['lead.full-access']) {
        return { ...member, canBeAssigned: true };
      }

      return { ...member, canBeAssigned: false };;
    });
  }, [assignedIds, orgMembers]);

  const checklistAssignees = useMemo(() => {
    return assignees.map((member) => {
      const { effectivePermissions: permissions, id } = member;

      // Org member has access to all leads if lead.all is true.
      if (permissions['lead.all']) return member;
      // Org member has access to leads but only leads assigned to
      // or created by org member.
      if (permissions['lead.full-access']) {
        // If org member is in list of lead assignments
        if (assignedIds.has(id)) return member;
      }
      return { ...member, canBeAssigned: false };
    });
  }, [assignedIds, assignees]);

  const assignments = useMemo(() => {
    return orgMembers.filter((m) => assignedIds.has(m.id));
  }, [assignedIds, orgMembers]);

  const addAssignment = async (member) => {
    dispatch({
      type: CREATE_LEAD_ASSIGNMENT,
      payload: {
        variables: {
          input: {
            loginId: member?.id,
            leadId: Number(leadId),
          },
        },
        orgId,
      },
    });
  };

  const removeAssignment = async (member) => {
    dispatch({
      type: DELETE_LEAD_ASSIGNMENT,
      payload: {
        variables: {
          input: {
            loginId: member?.id,
            leadId: Number(leadId),
          },
        },
        orgId,
      },
    });
  };

  const handleDeleteLead = () => {
    setIsDeleteConfirmOpened(true);
  };

  const commitDeleteLead = () => {
    dispatch(deleteLead({ id: leadId }));
    setIsDeleting(true);
  };

  useEffect(() => {
    if (!leadData && isDeleting) {
      history.push(`/app/v2/${orgId}/leads`);
    }
  }, [leadData]);

  useEffect(() => {
    // Scroll to top if tab changes.
    if(tabsRef.current) scrollToElementIfNeeded(tabsRef.current);
  }, [tab]);

  useEffect(() => {
    dispatch(fetchLead({ orgId, leadId }));
    dispatch(getInvoiceTemplates({ orgId }));
    dispatch(getQuoteTemplates({ orgId }));
    dispatch(getChecklistItems({ orgId, leadId }));
    dispatch(getLeadComments({ orgId, leadId }));
    getLeadCustomPreferences();
    getDocumentTemplates();
    fetchOrgMembers();
    getLeadAssignments({ orgId, leadId });
    fetchLeadChecklistMappingSettings();
  }, []);

  const handleStartTimeChange = (value) => {
    const time = timeFormatValidation(value);

    if (!time || time === startTime) return;

    const currentEnd = dayjs(endTime || '10:00:00', 'HH:mm:ss');
    const newStart = dayjs(time, 'HH:mm:ss');

    if (!endTime || newStart.isSameOrAfter(currentEnd)) {
      const newEnd = newStart.add(1, 'hour');
      updateLeadFields({ startTime: time, endTime: newEnd.format('HH:mm:ss') });
    } else {
      updateLeadFields({ startTime: time });
    }
  };

  const handleEndTimeChange = (value) => {
    // #TODO: Implement end time change logic.
    const time = timeFormatValidation(value);

    if (!time || time === endTime) return;

    updateLeadFields({ endTime: time });
  };

  const updateEventDate = (data) => {
    updateLeadFields({ weddingDate: dayjs(data, dateFormatLabel).format('YYYY-MM-DD') });
  };

  const updateInquiryDate = (data) => {
    updateLeadFields({ dateInquiry: dayjs(data, dateFormatLabel).format('YYYY-MM-DD') });
  };

  const updateLastContactedDate = (data) => {
    updateLeadFields({ lastContactedDate: dayjs(data, dateFormatLabel).format('YYYY-MM-DD') });
  };

  const updateChecklistTitle = (taskId, name) => {
    updateChecklistItemDispatch({
      orgId, leadId, checklistItemId: taskId,
      updateData: { name },
    });
  };

  const updateChecklistDueDate = (taskId, { type, value }) => {
    if (type === 'date') {
      const dueDate = value ? dayjs(value).format('YYYY-MM-DD') : null;
      if (!validateChecklistField('dueDate', dueDate)) return;
      updateChecklistItemDispatch({
        orgId, leadId, checklistItemId: taskId,
        updateData: { dueDate, timeframe: null },
      });
    } else if (type === 'timeframe') {
      if (!validateChecklistField('timeframe', value)) return;
      updateChecklistItemDispatch({
        orgId, leadId, checklistItemId: taskId,
        updateData: { timeframe: value ?? null },
      });
    }
  };

  const updateChecklistDetails = (taskId, details) => {
    if (!validateChecklistField('details', details)) return;
    updateChecklistItemDispatch({
      orgId, leadId, checklistItemId: taskId,
      updateData: { details },
    });
  };

  const updateChecklistCompleted = (taskId, completed) => {
    if (!validateChecklistField('completed', completed)) return;
    updateChecklistItemDispatch({
      orgId, leadId, checklistItemId: taskId,
      updateData: { completed },
    });
  };

  const updateChecklistAssignmentNote = (taskId, value) => {
    if (!validateChecklistField('assignmentNote', value)) return;
    updateChecklistItemDispatch({
      orgId, leadId, checklistItemId: taskId,
      updateData: { assignmentNote: value },
    });
  };

  const handleAddChecklistAssignment = (loginId, taskId) => {
    dispatch(addChecklistAssignment(orgId, leadId, loginId, taskId ));
  };

  const handleRemoveChecklistAssignment = (loginId, taskId) => {
    dispatch(removeChecklistAssignment(orgId, leadId, loginId, taskId));
  };

  const handleChecklistItemReorder = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    reorderChecklistItemDispatch({
      orgId,
      leadId,
      activeId: active.id,
      overId: over.id,
      prevItems: checklistItems,
    });
  };

  const getChecklistComments = (checklistItemId) => {
    dispatch(getChecklistCommentsAction(orgId, leadId, checklistItemId));
  };

  const handleAddChecklistComment = (checklistItemId, comment) => {
    dispatch(addChecklistComment(orgId, leadId, checklistItemId, comment));
  };

  const handleUpdateChecklistComment = (checklistItemId, commentId, comment) => {
    dispatch(updateChecklistComment(orgId, leadId, checklistItemId, commentId, comment));
  };

  const handleDeleteChecklistComment = (checklistItemId, commentId) => {
    dispatch(deleteChecklistComment(orgId, leadId, checklistItemId, commentId));
  };

  const handleGetTaskReminders = (taskId) => {
    dispatch(getChecklistReminders(orgId, leadId, taskId));
  };

  const handleCreateTaskReminder = (taskId, input) => {
    dispatch(createChecklistReminder(orgId, leadId, taskId, input));
  };

  const handleUpdateTaskReminder = (taskId, definitionId, input) => {
    dispatch(updateChecklistReminder(orgId, leadId, taskId, definitionId, input));
  };

  const handleDeleteTaskReminder = (taskId, definitionId) => {
    dispatch(deleteChecklistReminder(orgId, leadId, taskId, definitionId));
  };

  const handleContactWrapperBlur = (e) => {
    const next = e.relatedTarget;

    if (contactRef.current && next && contactRef.current.contains(next)) {
      return;
    }

    setEditingContactName(false);
  };

  const handleServiceTypeChange = (e) => {
    dispatch(getLeadChecklistTemplates(orgId));

    const newServiceType = Number(e.target.value);
    if (newServiceType < 0) {
      window.location.href = `/app/pro/${orgId}/settings/leads`;
      return;
    }

    if (serviceType !== newServiceType) {
      let leadChecklistTemplate;
      const checklistMapping = leadChecklistMappingSettings
        ?.list
        ?.find((mapping) => mapping?.lead_custom_preference_id === newServiceType);
      if (checklistMapping) {
        if (checklistMapping?.lead_checklist_template_id !== null) {
          leadChecklistTemplate = templateList
            ?.find((template) => template.id === checklistMapping.lead_checklist_template_id);
        }
      } else if (leadChecklistMappingSettings?.default !== null) {
        leadChecklistTemplate = leadChecklistMappingSettings.default;
      }

      if (leadChecklistTemplate) {
        setChecklistTemplate(leadChecklistTemplate);
        setChecklistTemplateModal(true);
      }
    }

    updateLeadFields({ serviceTypeId: newServiceType });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="container">
      <div className="page-back">
        <button
          className="back-button"
          onClick={goBack}
        >
          <FontAwesomeIcon icon={['fa', 'arrow-left']} />
          Back
        </button>
      </div>
      <section className="page-title page-title--actions">
        <h1 className="title">{leadFullName}</h1>

        <div className="page-title__actions">
          <LeadRecordActionButton
            orgId={orgId}
            lead={{ ...lead, location }}
            onMarkAsLost={() => setArchiveOpened(true)}
            projectCount={projects.length}
            onDeleteLead={handleDeleteLead}
            setTab={setTab}
            addNewTask={() => addTask()}
            openFileModal={() => setShowNewFileModal(true)}
            openNewEmailModal={() => setOpenNewEmailModal(true)}
          />
        </div>
      </section>
      <div className="lead-container">
        <div className={clsx('lead-content')}>
          <div ref={tabsRef} />
          <HorizontalMenu
            items={tabs}
            isResponsive
          />

          {tab === ACTIVITY_LOG && (
            <LeadRecordActivity
              comments={leadComments}
              timeZone={userInfo?.timezone || DEFAULT_TIMEZONE}
              onAddComment={handleAddComment}
              onUpdateComment={handleUpdateComment}
              onDeleteComment={handleDeleteComment}
              handleActivityClick={handleActivityClick}
            />
          )}
          {tab === CHECKLIST &&
            (isLoading || !checklists ? (
              <Loading />
            ) : checklistItems.length ? (
              <>
                <button
                  className="button button--sm add-additional-checklist"
                  onClick={openChecklistTemplateModal}
                >
                  Add Checklist
                </button>
                <Checklist
                  list={checklistItems}
                  currentLoginId={userInfo?.id}
                  timeZone={userInfo?.timezone || DEFAULT_TIMEZONE}
                  dateFormat={orgDateFormat}
                  timeFormat={orgTimeFormat}
                  assignablePeople={checklistAssignees}
                  assignments={checklistAssignments}
                  reminders={checklistRemindersByTask}
                  comments={commentsByChecklistItemId}
                  onChangeTitle={updateChecklistTitle}
                  onChangeDueDate={updateChecklistDueDate}
                  onChangeDetails={updateChecklistDetails}
                  onCheck={updateChecklistCompleted}
                  onDelete={handleDeleteTask}
                  onAddItem={addTask}
                  onChangeAssignmentNote={updateChecklistAssignmentNote}
                  onAssign={handleAddChecklistAssignment}
                  onUnassign={handleRemoveChecklistAssignment}
                  isSortable
                  onReorder={handleChecklistItemReorder}
                  getComments={getChecklistComments}
                  addComment={handleAddChecklistComment}
                  editComment={handleUpdateChecklistComment}
                  deleteComment={handleDeleteChecklistComment}
                  getReminders={handleGetTaskReminders}
                  createTaskReminder={handleCreateTaskReminder}
                  updateTaskReminder={handleUpdateTaskReminder}
                  deleteTaskReminder={handleDeleteTaskReminder}
                  collapseOnOutsideClick
                  preselectedTask={selectedItemId}
                />
                <button
                  className="button--unstyled gold button--add"
                  onClick={() => addTask()}
                >
                  Add Task
                </button>
                <APModal
                  open={templateModal}
                  onClose={() => setTemplateModal(false)}
                  title="Select sales workflow."
                  name="select-checklist"
                  subtitle={(
                    <>
                      Sales Workflows allow you to create client-specific checklists
                      so you never miss a beat when setting consultations or following
                      up with new leads. Create a unique user-specific workflow or
                      choose from one of your pre-created sales workflow templates
                      below. To create a new sales workflow template click
                      <a
                        href={`/app/pro/${orgId}/settings/templates/lead-checklist-templates`}
                      >
                        &nbsp;here.
                      </a>
                    </>
                  )}
                  actions={(
                    <button
                      type="button"
                      className="button"
                      onClick={handleCreateChecklistFromTemplate}
                      disabled={!selectedTemplate}
                    >
                      Add
                    </button>
                  )}
                >
                  <FormSelect
                    name="Select template"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    {templateList.length > 0 ?
                      templateList.map((template, index) => (
                        <option
                          value={template.id}
                          key={`template_${index}`}
                        >
                          {template.name}
                        </option>
                      ),
                    ) : [(
                      <button
                        onClick={() => {
                          window.location = `/app/pro/${orgId}/settings/templates/lead-checklist-templates`;
                        }}
                        className="button--unstyled option"
                      >
                        <strong className="text-gold">Create a sales workflow template</strong>
                        &nbsp;for your go-to lead tasks and you'll never have to
                        start from scratch again
                      </button>
                    )]}
                  </FormSelect>
                </APModal>
              </>
            ) : (
              <>
                <ChecklistLanding
                  openChecklistTemplateModal={openChecklistTemplateModal}
                  addTask={() => addTask()}
                />
                <APModal
                  open={templateModal}
                  onClose={() => setTemplateModal(false)}
                  title="Select sales workflow."
                  name="select-checklist"
                  subtitle={(
                    <>
                      Sales Workflows allow you to create client-specific checklists
                      so you never miss a beat when setting consultations or following
                      up with new leads. Create a unique user-specific workflow or
                      choose from one of your pre-created sales workflow templates
                      below. To create a new sales workflow template click
                      <a
                        href={`/app/pro/${orgId}/settings/templates/lead-checklist-templates`}
                      >
                        &nbsp;here.
                      </a>
                    </>
                  )}
                  actions={(
                    <button
                      type="button"
                      className="button"
                      onClick={handleCreateChecklistFromTemplate}
                      disabled={!selectedTemplate}
                    >
                      Add
                    </button>
                  )}
                >
                  <FormSelect
                    name="Select template"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    {templateList.length > 0 ?
                      templateList.map((template, index) => (
                        <option
                          value={template.id}
                          key={`template_${index}`}
                        >
                          {template.name}
                        </option>
                      ),
                    ) : [(
                      <button
                        onClick={() => {
                          window.location = `/app/pro/${orgId}/settings/templates/lead-checklist-templates`;
                        }}
                        className="button--unstyled option"
                      >
                        <strong className="text-gold">Create a sales workflow template</strong>
                        &nbsp;for your go-to lead tasks and you'll never have to
                        start from scratch again
                      </button>
                    )]}
                  </FormSelect>
                </APModal>
              </>
            )
          )}
          {tab === FILES && (
            <LeadRecordDocuments
              isLoading={isLoading}
              documents={documentList}
              documentTemplates={documentTemplates}
              quotes={quotesList}
              quoteTemplates={quoteTemplates}
              invoices={invoicesList}
              invoiceTemplates={invoiceTemplates}
              leadId={leadId}
              showNewFileModal={showNewFileModal}
              setShowNewFileModal={setShowNewFileModal}
              projectId={v2ClientProject?.id}
            />
          )}
          {tab === NOTES && (
            <LeadRecordNotes
              orgId={orgId}
              leadId={leadId}
              notes={notes}
              updateNotes={(value) => updateLeadFields({ notes: value })}
              userDefinedFields={userDefinedFields}
            />
          )}
          {tab === EMAILS && (
            <ThreadsCenter
              orgId={orgId}
              leadId={leadId}
              userInfo={userInfo}
              orgMembers={orgMembers}
              currentLead={{ ...lead, contactName: leadFullName, contactEmail: leadEmail }}
              readOnlyMode={readOnlyMode}
              hideBackButton
              hideContactInfo
              openNewEmail={openNewEmailModal}
              onNewEmailOpened={() => setOpenNewEmailModal(false)}
            />
          )}
        </div>
        <div className={clsx('lead-sidebar')}>
          <div className="lead-sidebar__status">
            <FormSelect
              id="lead-status"
              name="Status"
              value={leadCustomStatusId}
              onChange={(e) => {
                const selectedStatus = customStatuses.find(
                  (s) => String(s.id) === String(e.target.value),
                );
                if (selectedStatus?.name === 'Lost') {
                  setArchiveOpened(true);
                  return;
                }
                updateLeadFields({ leadCustomStatusId: e.target.value });
              }}
            >
              {customStatuses.map(option => (
                <option
                  value={option.id}
                  key={option.id}
                >
                  {option.name}
                </option>
              ))}
            </FormSelect>
          </div>
          <div className="lead-sidebar__info">
            <div className="lead-sidebar__img">
              <Avatar
                photo={avatarImage}
                text={avatarName}
                size={90}
                borderSize={5}
                className="avatar--transparent avatar--dark"
              />
            </div>
            <div className="lead-sidebar__contact">
              {!isEditingContactName ? (
                <button
                  type="button"
                  className="lead-sidebar__contact-field lead-sidebar__contact-field--name button--unstyled"
                  onClick={() => setEditingContactName(true)}
                >
                  {isLoading ? '' : contactName}
                </button>
              ) : (
                <div
                  ref={contactRef}
                  onBlur={handleContactWrapperBlur}
                >
                  <div className="lead-sidebar__contact-field">
                    <InputField
                      name="first_name"
                      placeholder="First Name"
                      showLabel={false}
                      defaultValue={contactFirstName}
                      onBlur={(e) => {
                        const newFirstName = e.target.value.trim();
                        if (newFirstName !== contactFirstName) {
                          const invalidFields = updateLeadFields({ contactFirstName: newFirstName || '' });
                          if (invalidFields.includes('contactFirstName')) {
                            e.target.value = contactFirstName;
                          }
                        }
                      }}
                    />
                  </div>
                  <div className="lead-sidebar__contact-field">
                    <InputField
                      name="last_name"
                      placeholder="Last Name"
                      showLabel={false}
                      defaultValue={contactLastName}
                      onBlur={(e) => {
                        const newLastName = e.target.value.trim();
                        if (newLastName !== contactLastName) {
                          const invalidFields = updateLeadFields({ contactLastName: newLastName || '' });
                          if (invalidFields.includes('contactLastName')) {
                            e.target.value = contactLastName;
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="lead-sidebar__contact-field lead-sidebar__contact-field--email">
                <ContactField
                  name="lead_email"
                  type="email"
                  placeholder="Email"
                  value={leadEmail}
                  typeValue={primaryEmail?.apLabelId === 'custom' ? primaryEmail?.fieldLabel : primaryEmail?.apLabelId}
                  onBlur={handleEmailUpdate}
                  onTypeChange={handleEmailTypeChange}
                />
              </div>
              <div className="lead-sidebar__contact-field">
                <ContactField
                  name="lead_phone"
                  type="phone"
                  placeholder="Phone"
                  value={leadPhone}
                  typeValue={primaryPhone?.apLabelId === 'custom' ? primaryPhone?.fieldLabel : primaryPhone?.apLabelId}
                  onBlur={handlePhoneUpdate}
                  onTypeChange={handlePhoneTypeChange}
                />
              </div>
            </div>
          </div>

          <Accordion
            title="Details"
            className="lead-details"
            open={!isMobile}
          >
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Project Name</div>
              <div className="lead-sidebar__value">
                <InputField
                  name="project_name"
                  showLabel={false}
                  defaultValue={projectName}
                  onBlur={(e) => {
                    const newValue = e.target.value;
                    if (newValue !== projectName) {
                      const invalidFields = updateLeadFields({ projectName: newValue });
                      if (invalidFields.includes('projectName')) {
                        e.target.value = projectName || '';
                      }
                    }
                  }}
                />
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Project Type</div>
              <div className="lead-sidebar__value">
                <FormSelect
                  name="Project Type"
                  value={projectType}
                  disabled={shouldLockProjectType}
                  emptyValue="Select…"
                  onChange={(e) => {
                    updateLeadFields({ projectType: e.target.value });
                  }}
                >
                  {PROJECT_TYPES.map((option, index) => (
                    <option value={option.value}
                      key={`option_${index}`}
                    >
                      {option.label}
                    </option>
                  ))}
                </FormSelect>
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Service Type</div>
              <div className="lead-sidebar__value">
                <FormSelect
                  name="Service Type"
                  value={serviceType}
                  emptyValue="Select…"
                  onChange={handleServiceTypeChange}
                >
                  {serviceTypes.map((option, index) => (
                    <option value={option.value}
                      key={`option_${index}`}
                    >
                      {option.label}
                    </option>
                  ))}
                </FormSelect>
                {checklistTemplate && (
                  <APModal
                    open={checklistTemplateModal}
                    onClose={() => setChecklistTemplateModal(false)}
                    name="service-type-confirmation"
                    actions={(
                      <>
                        <button
                          type="button"
                          className="button"
                          onClick={() => {
                            setChecklistTemplateModal(false);
                            dispatch(createChecklistFromTemplate({
                              orgId,
                              leadId,
                              templateId: checklistTemplate.id,
                            }));
                          }}
                        >
                          Add Items
                        </button>
                        <button
                          type="button"
                          className="button"
                          onClick={() => setChecklistTemplateModal(false)}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  >
                    <p>
                      Do you want to use the following checklist template:
                      "{checklistTemplate.name}" to add checklist items?
                    </p>
                  </APModal>
                )}
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Event Date</div>
              <div className="lead-sidebar__value">
                <CalendarWidget
                  name="Event Date"
                  date={eventDate ? dayjs(eventDate).format(dateFormatLabel) : ''}
                  pickerDate={eventDate ? dayjs(eventDate).format(DEFAULT_DATE_FORMAT) : ''}
                  dateFormatLabel={dateFormatLabel}
                  setDate={updateEventDate}
                  showNextYears
                  hasClear={false}
                />
              </div>
            </div>
            <div className="lead-sidebar__checkbox">
              <Clickwrap
                name="all_day_event"
                checked={allDayEvent}
                className="clickwrap--view-option small"
                onChange={() => {
                  if (!allDayEvent === false) {
                    updateLeadFields({
                      allDay: !allDayEvent,
                      startTime: '09:00:00',
                      endTime: '10:00:00',
                    });
                  } else {
                    updateLeadFields({ allDay: !allDayEvent });
                  }
                }}
              />
              <label htmlFor="all_day_event">All day event</label>
            </div>
            {!allDayEvent && (
              <>
                <div className="lead-sidebar__row">
                  <div className="lead-sidebar__label">Start Time</div>
                  <div className="lead-sidebar__value">
                    <TimeSettingFormItem
                      key={`start-time-${startTime}-${timeFormat}`}
                      name="Start time"
                      setTime={handleStartTimeChange}
                      time={startTime ?? '09:00:00'}
                      timeFormat={timeFormat}
                      timeList={timeList}
                    />
                  </div>
                </div>
                <div className="lead-sidebar__row">
                  <div className="lead-sidebar__label">End Time</div>
                  <div className="lead-sidebar__value">
                    <TimeSettingFormItem
                      key={`end-time-${endTime}-${timeFormat}`}
                      name="End time"
                      setTime={handleEndTimeChange}
                      time={endTime ?? '10:00:00'}
                      timeList={timeList}
                      timeFormat={timeFormat}
                      error={!allDayEvent && !endTime}
                    />
                  </div>
                </div>
              </>
            )}
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Guest Count</div>
              <div className="lead-sidebar__value">
                <InputField
                  name="guest_count"
                  type="number"
                  showLabel={false}
                  defaultValue={guestCount}
                  onBlur={(e) => {
                    const newValue = e.target.value;
                    if (newValue !== guestCount) {
                      const invalidFields = updateLeadFields({ estimatedGuestCount: newValue });
                      if (invalidFields.includes('estimatedGuestCount')) {
                        e.target.value = guestCount || '';
                      }
                    }
                  }}
                />
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Venue</div>
              <div className="lead-sidebar__value">
                <InputField
                  name="venue"
                  showLabel={false}
                  defaultValue={venue}
                  onBlur={(e) => {
                    const newValue = e.target.value;
                    if (newValue !== venue) {
                      const invalidFields = updateLeadFields({ venueName: newValue });
                      if (invalidFields.includes('venueName')) {
                        e.target.value = venue || '';
                      }
                    }
                  }}
                />
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Location</div>
              <div className="lead-sidebar__value">
                <LocationSelect
                  location={location}
                  setLocation={(loc) => {
                    if (!loc) {
                      updateLocationField(null);
                      return;
                    };
                    const preparedData = objToCamelCase(prepareLocationForApi(loc));
                    updateLocationField(preparedData);
                  }}
                />
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Budget</div>
              <div className="lead-sidebar__value">
                <CurrencyInput
                  name="budget"
                  showLabel={false}
                  cents={budget}
                  currency={currency}
                  onChange={(newValue) => {
                    if (newValue !== budget) {
                      updateLeadFields({ estimatedBudget: newValue });
                    }
                  }}
                />
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Time Zone</div>
              <div className="lead-sidebar__value">
                <FormSelect
                  name="Timezone"
                  id="timezone"
                  value={timezone}
                  onChange={(e) => {
                    updateLeadFields({ timezone: e.target.value });
                  }}
                  showSearch
                  labelHidden
                >
                  {TIME_ZONES_ORDER.map((option, index) => (
                    <optgroup label={option.value}
                      key={`option_${index}`}
                    >
                      {TIME_ZONES.filter((tz) => tz.group === option.value).map(
                        (optionTZ, indexTZ) => (
                          <option
                            value={optionTZ.value}
                            key={`optionTZ_${option.value}_${indexTZ}`}
                          >
                            {optionTZ.label}
                          </option>
                        ),
                      )}
                    </optgroup>
                  ))}
                </FormSelect>
              </div>
            </div>
            <div className="lead-sidebar__divider" />
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Inquiry Date</div>
              <div className="lead-sidebar__value">
                <CalendarWidget
                  name="Inquiry Date"
                  date={inquiryDate ? dayjs(inquiryDate).format(dateFormatLabel) : ''}
                  pickerDate={inquiryDate ? dayjs(inquiryDate).format(DEFAULT_DATE_FORMAT) : ''}
                  dateFormatLabel={dateFormatLabel}
                  setDate={updateInquiryDate}
                  showNextYears
                  hasClear={false}
                />
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Last Contacted</div>
              <div className="lead-sidebar__value">
                <CalendarWidget
                  name="Last Contacted Date"
                  date={lastContactedDate ? dayjs(lastContactedDate).format(dateFormatLabel) : ''}
                  pickerDate={lastContactedDate ? dayjs(lastContactedDate).format(DEFAULT_DATE_FORMAT) : ''}
                  dateFormatLabel={dateFormatLabel}
                  setDate={updateLastContactedDate}
                  showNextYears
                  hasClear={false}
                />
              </div>
            </div>
            <div className="lead-sidebar__row">
              <div className="lead-sidebar__label">Lead Source</div>
              <div className="lead-sidebar__value">
                <FormSelect
                  name="Lead Source"
                  value={sourceType}
                  emptyValue="Select…"
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value < 0) {
                      window.location.href = `/app/pro/${orgId}/settings/leads`;
                      return;
                    }
                    updateLeadFields({ leadSourceId: value });
                  }}
                >
                  {sourceTypes.map((option, index) => (
                    <option value={option.value}
                      key={`option_${index}`}
                    >
                      {option.label}
                    </option>
                  ))}
                </FormSelect>
              </div>
            </div>
          </Accordion>
          {projectType === 'wedding' && (
            <Accordion title="The Couple">
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <FormSelect
                    name=""
                    value={primaryPartyRole}
                    emptyValue="Role"
                    onChange={(e) => {
                      updateLeadFields({
                        party: nextParty(0, 'role', e.target.value),
                      });
                    }}
                  >
                    {getRoleOptions()}
                  </FormSelect>
                </div>
              </div>
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <InputField
                    name="Primary Party FirstName"
                    showLabel={false}
                    defaultValue={primaryPartyFirstName}
                    placeholder="First"
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== primaryPartyFirstName) {
                        updateLeadFields({
                          party: nextParty(0, 'firstName', newValue),
                        });
                      }
                    }}
                  />
                </div>
              </div>
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <InputField
                    name="Primary Party LastName"
                    showLabel={false}
                    defaultValue={primaryPartyLastName}
                    placeholder="Last"
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== primaryPartyLastName) {
                        updateLeadFields({
                          party: nextParty(0, 'lastName', newValue),
                        });
                      }
                    }}
                  />
                </div>
              </div>
              <div className="party-separator">
                <HeaderString
                  value="&"
                  color="gray"
                />
              </div>
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <FormSelect
                    name=""
                    value={secondaryPartyRole}
                    emptyValue="Role"
                    onChange={(e) => {
                      updateLeadFields({
                        party: nextParty(1, 'role', e.target.value),
                      });
                    }}
                  >
                    {getRoleOptions()}
                  </FormSelect>
                </div>
              </div>
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <InputField
                    name="Secondary Party FirstName"
                    showLabel={false}
                    defaultValue={secondaryPartyFirstName}
                    placeholder="First"
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== secondaryPartyFirstName) {
                        updateLeadFields({
                          party: nextParty(1, 'firstName', newValue),
                        });
                      }
                    }}
                  />
                </div>
              </div>
              <div className="lead-sidebar__row">
                <div className="lead-sidebar__value lead-sidebar__value--full">
                  <InputField
                    name="Secondary Party LastName"
                    showLabel={false}
                    defaultValue={secondaryPartyLastName}
                    placeholder="Last"
                    onBlur={(e) => {
                      const newValue = e.target.value;
                      if (newValue !== secondaryPartyLastName) {
                        updateLeadFields({
                          party: nextParty(1, 'lastName', newValue),
                        });
                      }
                    }}
                  />
                </div>
              </div>
            </Accordion>
          )}
          <Accordion title="Contact info">
            <LeadRecordContactInfo
              orgId={orgId}
              leadId={leadId}
              contact={contact}
              serviceTypes={serviceTypes}
              sourceTypes={sourceTypes}
              contactCompanyName={contactCompanyName}
              role={role}
              emails={emails}
              phones={phones}
              addresses={addresses}
              websites={websites}
              updateLeadFields={updateLeadDispatch}
            />
          </Accordion>
          <Accordion title="Lead Assignments">
            <div className="lead-sidebar__row lead-sidebar__row--assignments">
              <MemberAssignment
                members={assignees}
                assignments={assignments}
                onAdd={addAssignment}
                onRemove={removeAssignment}
              />
            </div>
          </Accordion>
          {projects.length > 0 && (
            <>
              {projects.map(({
                name,
                id: projectId,
                weddingDate,
                creationStatus,
                profileImage,
                shortname,
              }) => {
                return (
                  <Accordion title="Project">
                    <div className="lead-sidebar__row lead-sidebar__row--project">
                      <LeadRecordProjectItem
                        name={name}
                        projectId={projectId}
                        weddingDate={weddingDate}
                        creationStatus={creationStatus}
                        image={profileImage}
                        shortname={shortname}
                        dateFormat={getDayjsDateFormat(organization.datePreference, { month: 'long' })}
                      />
                    </div>
                  </Accordion>
                );
              })}
            </>
          )}
        </div>
      </div>
      <DeleteConfirmation
        show={isDeleteConfirmOpened}
        handleClose={() => {
          setIsDeleteConfirmOpened(false);
        }}
        handleConfirm={commitDeleteLead}
        size="xs"
        confirmText="DELETE"
        requireConfirmation
      >
        <p>
          Deleting this lead will remove it forever. Are you sure?
          Type DELETE to confirm.
        </p>
      </DeleteConfirmation>
      <ArchiveModal
        leadId={leadId}
        lostReasons={lostReasons}
        lostStatus={lostStatus}
        active={leadCustomStatusId}
        archiveOpened={archiveOpened}
        setArchiveOpened={setArchiveOpened}
      />
    </div>
  );
};

const ChecklistLanding = ({ openChecklistTemplateModal, addTask }) => {

  return (
    <div className="leads-checklist-landing">
      <p>
        Your booking checklist helps you stay organized by turning your workflow into a
        clear, step-by-step process. Assign tasks, track progress, and see who’s doing
        what—all with deadlines that sync to your calendar and built-in tools for team
        collaboration. It’s everything you need to keep your projects moving forward.
        &nbsp;
        <a
          target="_blank"
          rel="noreferrer"
          // eslint-disable-next-line max-len
          href="https://help.aisleplanner.com/en/articles/2425618-using-your-sales-workflow-to-keep-you-on-task-and-drive-sales"
        >
          Learn more here
        </a>.
        <div className="button-group button-group--left">
          <button
            className="button button--sm"
            onClick={openChecklistTemplateModal}
          >
            Add Checklist
          </button>
          <button
            className="button button--sm"
            onClick={() => addTask()}
          >
            Add Task
          </button>
        </div>
      </p>
    </div>
  );
};

export default LeadRecord;
